import { useEffect, useReducer, useState } from 'react';
import { toast } from 'react-toastify';
import {
  AuctionRegApi,
  AuctionUpdate,
  TournamentAll,
  TournamentGetByIdGet,
  UploadImage,
} from '../../redux/auth-reducer/auth-service';
import { ImageUrl } from '../../utils/config/config';
import { readApiMessage } from '../../utils/helper/helper';
import { rejectImagePick } from '../../utils/helper/imageUpload';
import { summarizePrizeSplit } from '../../utils/helper/prizeSplit';
import { describeAuctionFit } from '../../utils/helper/auctionFit';
import {
  auctionFormFromRow,
  auctionFormProblem,
  auctionFormReducer,
  blankAuctionForm,
  createAuctionPayload,
  selectableContests,
  updateAuctionPayload,
} from '../../utils/helper/auctionForm';

const initForm = (auction) => (auction ? auctionFormFromRow(auction) : blankAuctionForm());

/**
 * The auction form of Create Auction and of the Update Auction modal: the state and rules of
 * auctionForm.js, plus the requests they need. Each screen lays its fields out itself, with the
 * components in AuctionFormFields.js.
 *
 * @param {{auction?: Object, onSaved?: () => void}} options auction is the Auctions list row to edit;
 *   without one the form creates an auction. onSaved runs after the server has accepted a save.
 */
export function useAuctionForm({ auction, onSaved } = {}) {
  const [values, dispatch] = useReducer(auctionFormReducer, auction, initForm);
  const [contestList, setContestList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    TournamentAll().then((response) => setContestList(response?.data?.data?.dataList || []));
  }, []);

  // The selected contest's details: the teams of a contest just picked, and the contest's start for
  // the fit note. A reply for a contest no longer selected is dropped by the reducer.
  useEffect(() => {
    if (!values.tournamentId) return;
    TournamentGetByIdGet(values.tournamentId).then((details) => dispatch({ type: 'contestLoaded', details }));
  }, [values.tournamentId]);

  const userId = JSON.parse(localStorage.getItem('isUser'))?.userId;
  const contests = selectableContests(contestList, userId, values);

  // The prize split as it will be sent, read on every render: the running total and the check before
  // saving both come from it (#146).
  const winnerTitles = values.winners.map((winner) => winner.title);
  const prizeSplit = summarizePrizeSplit(
    values.xcuttaTake,
    values.winners.map((winner) => winner.percentage)
  );

  // #171: whether the lots fit before bidding closes, under Bid Time. Every team listed is a lot,
  // The Field included, and the contest is the one whose details have loaded.
  const auctionFit = describeAuctionFit({
    teamIds: values.teams.map((team) => team?.id),
    bidTime: values.bidTime,
    auctionStart: values.startDate,
    contestStart: values.contestDetails?.startDate ?? null,
  });

  const setField = (name, value) => dispatch({ type: 'field', name, value });

  const pickContest = (tournamentId) =>
    dispatch({
      type: 'contestPicked',
      contest: contests.find((contest) => String(contest.tournamentId) === String(tournamentId)) ?? null,
    });

  // Takes the file input, so a refused file can be cleared and re-picking it reports it again
  // (#251); a file the server would refuse is reported without being sent.
  const uploadImage = async (input) => {
    if (rejectImagePick(input)) return;
    const file = input.files[0];
    const formDataImage = new FormData();
    formDataImage.append('file', file);

    setUploading(true);
    const res = await UploadImage(formDataImage);
    setUploading(false);

    // The message is the stored file name only on success; a refusal's message is the reason, and
    // the logo the form already had stays.
    if (res?.data?.status === true) {
      setField('image', res.data.message);
    } else {
      toast.error(readApiMessage(res));
      input.value = '';
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    dispatch({ type: 'validated' });

    const problem = auctionFormProblem(values, prizeSplit, winnerTitles);
    if (problem) {
      toast.error(problem);
      return;
    }

    setSaving(true);
    const editing = values.mode === 'edit';
    const res = editing
      ? await AuctionUpdate(updateAuctionPayload(values, prizeSplit))
      : await AuctionRegApi(createAuctionPayload(values, prizeSplit));
    setSaving(false);

    // AuctionUpdate answers with the body, AuctionRegApi with the whole response. A refusal is read
    // with readApiMessage, which also names the field of an automatic 400 instead of showing nothing.
    const body = editing ? res : res?.data;
    if (body?.status === true) {
      toast.success(body.message);
      onSaved?.();
    } else {
      toast.error(readApiMessage(res));
    }
  };

  return {
    values,
    contests,
    prizeSplit,
    winnerTitles,
    auctionFit,
    imageUrl: values.image ? ImageUrl + values.image : null,
    saving,
    uploading,
    setField,
    pickContest,
    reorderTeams: (teams) => dispatch({ type: 'teamsReordered', teams }),
    setWinnerCount: (count) => dispatch({ type: 'winnerCount', count }),
    setWinnerPercentage: (index, value) => dispatch({ type: 'winnerPercentage', index, value }),
    setSecondaryMarket: (on) => dispatch({ type: 'secondaryMarket', on }),
    uploadImage,
    submit,
  };
}
